/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-unused-vars */
import LZ from 'lz-string';
import { Persistor, createTransform } from 'redux-persist';
import sessionStorage from 'redux-persist/lib/storage/session';
import { requestsStatusReducerKey } from '@altermaind/core/requests-status-management';
import { aspectRootReducerStateKey } from '@/aspect/state/rootReducer';

let _persistor: Persistor = null as unknown as Persistor;

const customKeysFromRequestStatusStateToRemove: string | string[] = [];

/**
 * This function removes custom keys from the request status state.
 *
 * @param {any} currentState - The current state of the request.
 * @returns {any} newState - The new state of the request after removing the custom keys.
 *
 * The function works as follows:
 * 1. Creates a copy of the current state.
 * 2. Iterates over all keys in the state.
 * 3. If the key is included in the list of custom keys to remove, it removes it from the new state.
 * 4. In the end, it returns the new state.
 */
const removeCustomKeysFromRequestStatusState = (currentState: any) => {
  const newState = { ...currentState };
  Object.keys(newState).forEach(key => {
    if (customKeysFromRequestStatusStateToRemove.includes(key)) {
      delete newState[key];
    }
  });
  return newState;
};

/* Removing otp calls from state so that, after refreshing,
 * the bottom border of the otp digits is black instead of green */
const removeOtpKeysFromState = (currentState: any) => {
  const newState = { ...currentState };
  Object.keys(newState).forEach(key => {
    if (key.indexOf('otp') >= 0) {
      delete newState[key];
    }
  });
  return newState;
};

/* Removing '_owner' field from react component which causes
 * serialization issues */
const removeReactComponentsFromSteps = (currentState: any) => {
  const newState = { ...currentState };
  newState.steps = newState.steps?.map((step: any) => {
    const newStep = { ...step };
    newStep.steps = step.steps?.map((innerStep: any) => {
      const newInnerStep = { ...innerStep };
      newInnerStep.Component = innerStep.Component?.map((component: any) => {
        const newComponent = { ...component };
        delete newComponent._owner;
        return newComponent;
      });
      return newInnerStep;
    });
    return newStep;
  });
  return newState;
};

const RequestStatusTransform = createTransform(
  (inboundState, _) => removeCustomKeysFromRequestStatusState(inboundState),
  (outboundState, _) => removeOtpKeysFromState({}),
  { whitelist: [requestsStatusReducerKey, aspectRootReducerStateKey] },
);

const WizardTransform = createTransform(
  (inboundState, _) => removeReactComponentsFromSteps(inboundState),
  (outboundState, _) => outboundState,
  { whitelist: [] },
);

const CompressAll = createTransform(
  (inboundState, _) => {
    const serializedPersistedState = LZ.compressToUTF16(JSON.stringify(inboundState));
    return serializedPersistedState;
  },
  (outboundState, _) => {
    const deserializedPersistedState = JSON.parse(LZ.decompressFromUTF16(outboundState));
    return deserializedPersistedState;
  },
);

export function setPersistor(persistor: Persistor) {
  _persistor = persistor;
}

export function getPersistor() {
  return _persistor;
}

export const rootPersistConfig = {
  key: 'root',
  storage: sessionStorage,
  transforms: [RequestStatusTransform, WizardTransform, CompressAll],
  blacklist: [requestsStatusReducerKey, aspectRootReducerStateKey, 'i18n'],
};
